import { Box, Link, Paper, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import LabeledTextField from '@/components/dataDisplay/LabeledTextField'
import { ConfigAuthorInfo } from '@/features/controller/configuration/components/ConfigAuthorInfo'
import type { ControllerConfigVersion } from '@/features/controller/configuration/types'
import { renderConfigVersionShortId } from '@/features/controller/configuration/utils/versionNames'
import { ControllerRouteInformation } from '@/features/controller/constants'
import { renderTimestamp } from '@/features/controller/utils/renderTimestamp'

interface ConfigVersionInfoProps {
  configVersion: ControllerConfigVersion
  controllerId: string
}

export function ConfigVersionInfo({ configVersion, controllerId }: Readonly<ConfigVersionInfoProps>) {
  const { t } = useTranslation()

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2}>
          <Box sx={{ flex: 1 }}>
            <CustomTypography color="grey.500" variant="caption">
              {t('controllers.configuration.version.version_id')}
            </CustomTypography>
            <CustomTypography>
              <Link
                component={RouterLink}
                to={ControllerRouteInformation.CONTROLLER_CONFIG_VIEW.navigationPath(controllerId, configVersion.id)}
              >
                {renderConfigVersionShortId(configVersion)}
              </Link>
            </CustomTypography>
          </Box>
          <Box sx={{ flex: 2 }}>
            <LabeledTextField
              label={t('controllers.configuration.version.sent_to_controller_at')}
              text={renderTimestamp(configVersion.sentToControllerAt)}
            />
          </Box>
        </Stack>
        <Stack direction="row" spacing={2}>
          <Box sx={{ flex: 1 }}>
            <LabeledTextField
              label={t('controllers.configuration.version.updated_at')}
              text={renderTimestamp(configVersion.createdAt)}
            />
          </Box>
          <Box sx={{ flex: 2 }}>
            <ConfigAuthorInfo configVersion={configVersion} />
          </Box>
        </Stack>
        {configVersion.comment && (
          <LabeledTextField label={t('controllers.configuration.comment')} text={configVersion.comment} />
        )}
      </Stack>
    </Paper>
  )
}
